/**
 * The properties panel.
 *
 * For precision, not for primary interaction - the customer drags on the
 * canvas and comes here when they want the logo exactly 8 mm from the edge.
 * §6.3.
 *
 * Every number here is a millimetre and says so, and every one commits with a
 * coalesce key, so holding the up-arrow on a field is one undo step rather
 * than forty.
 */

import { useEditor, useSelectedLayers } from '../state/store';
import { familyHas } from '../text/fonts';
import { FontField } from './FontField';
import { CornersField } from './Choices';
import { UNITS, formatLength, formatSize, fromMm, roundMm, stepIn, toMm, unitInfo } from '../model/units';
import type { ImageLayer, Layer, QrLayer, ShapeLayer, TextLayer } from '../model/types';
import { MIN_MODULE_MM, grid, moduleMm } from '../codes/qr';
import { fieldsFromValue } from '../codes/qr-types';
import { QrFields, QrKindSelect } from './QrFields';
import { dpiVerdict, effectiveDpi } from '../model/units';
import { knownAsset } from '../assets';
import { settle, shownRatio } from '../canvas/CropBox';
import {
  alignSelected,
  cannotConvert,
  convertToPath,
  distributeSelected,
  flipSelected,
  setQrContent,
  type Against,
  type Edge,
} from '../state/actions';
import { ColourField } from './ColourField';
import { FillField } from './FillField';
import { shapeDef, shapeGroups } from '../shapes/library';

export function Properties() {
  const doc = useEditor((s) => s.doc);
  const page = useEditor((s) => s.doc?.pages[s.pageIndex] ?? null);
  const selected = useSelectedLayers();

  if (!doc || !page) return <div className="tb-props" />;

  return (
    <div className="tb-props">
      {selected.length === 0 && <PageProperties />}
      {selected.length === 1 && selected[0] && <LayerProperties layer={selected[0]} />}

      {selected.length > 1 && (
        <div className="tb-props__empty">
          {selected.length} objects selected.
          <br />
          Line them up below, or pick one to change it.
        </div>
      )}

      {/*
        Outside the single-layer panel on purpose: lining things up is mostly
        something you do to several at once, and it was unreachable while it
        lived where only one selection could see it.
      */}
      {selected.length > 0 && <Arrange />}
    </div>
  );
}

function PageProperties() {
  const doc = useEditor((s) => s.doc)!;
  const page = useEditor((s) => s.doc!.pages[s.pageIndex])!;
  const unit = useEditor((s) => s.unit);
  const setUnit = useEditor((s) => s.setUnit);

  return (
    <>
      <section className="tb-props__section">
        <h2 className="tb-props__heading">{page.name}</h2>

        <Readout label="Size" value={formatSize(page.size.w, page.size.h, unit)} />
        <Readout label="Bleed" value={formatLength(doc.bleed, unit)} />
        <Readout label="Safe" value={formatLength(doc.safe, unit)} />

        <CornersField />

        {/*
          The same choice as the corner of the rulers. Here as well because this
          is where someone looking for "inches" goes first.
        */}
        <label className="tb-field">
          <span className="tb-field__label">Units</span>
          <select className="tb-input tb-units" value={unit} onChange={(event) => setUnit(event.target.value as typeof unit)}>
            {UNITS.map((each) => (
              <option key={each.id} value={each.id}>
                {each.name}
              </option>
            ))}
          </select>
        </label>
      </section>

      <div className="tb-props__empty">
        Nothing selected.
        <br />
        Pick an object on the card, or add one from the left.
      </div>
    </>
  );
}

function LayerProperties({ layer }: { layer: Layer }) {
  return (
    <>
      <section className="tb-props__section">
        {/* The type is the heading, in the same uppercase as every other
            section label. The layer's own name is the customer's words and is
            shown as they wrote it - putting "Name" in small caps next to
            "TEXT" makes a name look like a field. */}
        <h2 className="tb-props__heading">{describe(layer)}</h2>
        {layer.name && layer.name !== describe(layer) && <p className="tb-props__name">{layer.name}</p>}

        <Geometry layer={layer} />
      </section>

      {layer.type === 'shape' && <ShapeProperties layer={layer} />}
      {layer.type === 'text' && <TextProperties layer={layer} />}
      {layer.type === 'image' && <ImageProperties layer={layer} />}
      {layer.type === 'qr' && <QrProperties layer={layer} />}
    </>
  );
}

/**
 * A shape's own controls: which shape, how it is adjusted, and its two colours.
 *
 * The adjustment is whichever one the shape actually reads - a corner for a
 * rectangle, a number of points for a star, neither for a triangle. Offering
 * all of them and letting most do nothing is how a panel teaches people to
 * ignore it.
 */
/**
 * Lining things up, and turning them over.
 *
 * Shown for any selection, because the meaning changes rather than disappearing:
 * one object aligns to the card, several align to each other. A control that
 * greys out when one thing is selected reads as broken; one that does something
 * sensible reads as a tool.
 */
/**
 * Where a layer is and how big, in millimetres; its turn and its fade.
 * Shared by the settings panel and the position panel.
 */
export function Geometry({ layer }: { layer: Layer }) {
  const update = useEditor((s) => s.updateLayer);

  const set = (field: 'x' | 'y' | 'w' | 'h' | 'rotation' | 'opacity', value: number) =>
    update(layer.id, `Change ${field}`, (l) => ({ ...l, [field]: value }), {
      coalesce: `${layer.id}:${field}`,
    });

  return (
    <>
      <div className="tb-field__row">
        <Number label="X" value={layer.x} onChange={(v) => set('x', v)} />
        <Number label="Y" value={layer.y} onChange={(v) => set('y', v)} />
      </div>

      <div className="tb-field__row">
        <Number label="W" value={layer.w} onChange={(v) => set('w', Math.max(0, v))} />
        <Number label="H" value={layer.h} onChange={(v) => set('h', Math.max(0, v))} />
      </div>

      <div className="tb-field__row">
        <Number label="Turn" value={layer.rotation} unit="°" step={1} onChange={(v) => set('rotation', v)} />
        <Number
          label="Fade"
          value={Math.round(layer.opacity * 100)}
          unit="%"
          step={5}
          onChange={(v) => set('opacity', Math.max(0, Math.min(1, v / 100)))}
        />
      </div>
    </>
  );
}

export function Arrange() {
  const selected = useSelectedLayers();
  const many = selected.length > 1;
  const enough = selected.length > 2;

  // Label, the mark to draw, and the edge itself - said once, so the two rows
  // cannot disagree about which button means what.
  const edges: Array<{ label: string; mark: string; edge: Edge }> = [
    { label: 'Left', mark: 'left', edge: 'left' },
    { label: 'Centre', mark: 'centre-x', edge: 'centreX' },
    { label: 'Right', mark: 'right', edge: 'right' },
    { label: 'Top', mark: 'top', edge: 'top' },
    { label: 'Middle', mark: 'centre-y', edge: 'centreY' },
    { label: 'Bottom', mark: 'bottom', edge: 'bottom' },
  ];

  const row = (against: Against, title: (label: string) => string) => (
    <div className="tb-toggles">
      {edges.map((each) => (
        <button
          type="button"
          key={each.edge}
          className="tb-toggle"
          title={title(each.label)}
          onClick={() => alignSelected(each.edge, against)}
        >
          <AlignMark kind={each.mark} />
        </button>
      ))}
    </div>
  );

  return (
    <section className="tb-props__section">
      <h2 className="tb-props__heading">Arrange</h2>

      <div className="tb-field">
        <span className="tb-field__label">{many ? 'To each other' : 'To the card'}</span>
        {row('selection', (label) => label)}
      </div>

      {/*
        Aligning to the card is offered separately once there are several
        objects, because by then "left" has two reasonable meanings and the
        customer has to be able to say which.
      */}
      {many && (
        <div className="tb-field">
          <span className="tb-field__label">To the card</span>
          {row('page', (label) => `${label}, against the card`)}
        </div>
      )}

      <div className="tb-field">
        <span className="tb-field__label">Spread</span>
        <div className="tb-toggles">
          <button
            type="button"
            className="tb-toggle"
            disabled={!enough}
            title={enough ? 'Even gaps across' : 'Three or more objects to spread'}
            onClick={() => distributeSelected('h')}
          >
            ⇿
          </button>
          <button
            type="button"
            className="tb-toggle"
            disabled={!enough}
            title={enough ? 'Even gaps down' : 'Three or more objects to spread'}
            onClick={() => distributeSelected('v')}
          >
            ⇕
          </button>
          <button type="button" className="tb-toggle" title="Flip left to right" onClick={() => flipSelected('x')}>
            ⇋
          </button>
          <button type="button" className="tb-toggle" title="Flip top to bottom" onClick={() => flipSelected('y')}>
            ⇅
          </button>
        </div>
      </div>
    </section>
  );
}

/** A small diagram of what the button does, drawn rather than named. */
function AlignMark({ kind }: { kind: string }) {
  const bar = { fill: 'currentColor' };
  const rule = { stroke: 'currentColor', strokeWidth: 1.5 };

  const across = kind === 'left' || kind === 'centre-x' || kind === 'right';

  const line =
    kind === 'left' ? [2, 1, 2, 15]
    : kind === 'right' ? [16, 1, 16, 15]
    : kind === 'centre-x' ? [9, 1, 9, 15]
    : kind === 'top' ? [1, 2, 17, 2]
    : kind === 'bottom' ? [1, 14, 17, 14]
    : [1, 8, 17, 8];

  const bars = across
    ? [
        { x: kind === 'right' ? 6 : kind === 'centre-x' ? 3 : 4, y: 4, width: 10, height: 3 },
        { x: kind === 'right' ? 9 : kind === 'centre-x' ? 6 : 4, y: 9, width: 7, height: 3 },
      ]
    : [
        { x: 4, y: kind === 'bottom' ? 5 : 4, width: 3, height: 9 },
        { x: 10, y: kind === 'bottom' ? 8 : 4, width: 3, height: 6 },
      ];

  return (
    <svg width="18" height="16" viewBox="0 0 18 16" aria-hidden="true">
      <line x1={line[0]} y1={line[1]} x2={line[2]} y2={line[3]} {...rule} />
      {bars.map((box, i) => (
        <rect key={i} {...box} {...bar} rx="1" />
      ))}
    </svg>
  );
}

function ShapeProperties({ layer }: { layer: ShapeLayer }) {
  const update = useEditor((s) => s.updateLayer);

  const set = <K extends keyof ShapeLayer>(field: K, value: ShapeLayer[K]) =>
    update(layer.id, 'Change shape', (l) => ({ ...(l as ShapeLayer), [field]: value }), {
      coalesce: `${layer.id}:${String(field)}`,
    });

  const def = shapeDef(layer.shape);

  return (
    <section className="tb-props__section">
      {/* Not "Shape" - the section above already says that, and the layer's
          own name says which shape. This one is about how it looks. */}
      <h2 className="tb-props__heading">Appearance</h2>

      {/*
        A drawing has no kind to choose. The list used to be offered anyway,
        showing Rectangle because "drawn" is not in it - and choosing anything
        from it replaced the drawing with that shape.
      */}
      {layer.shape === 'free' ? (
        <PathControls layer={layer} />
      ) : (
        <>
          <label className="tb-field">
            <span className="tb-field__label">Kind</span>
            <select
              className="tb-input"
              value={layer.shape}
              onChange={(event) => set('shape', event.target.value as ShapeLayer['shape'])}
            >
              {shapeGroups().map(({ group, shapes }) => (
                <optgroup label={group} key={group}>
                  {shapes.map((shape) => (
                    <option value={shape.id} key={shape.id}>
                      {shape.name}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </label>

          {/*
            Any shape can become a drawing, and then be reshaped point by
            point - a star with one long arm, a speech bubble with its tail
            moved. Greyed with the reason when the shape has a hole in it.
          */}
          <button
            type="button"
            className="tb-btn tb-points__open"
            disabled={!!cannotConvert(layer) || layer.locked}
            title={cannotConvert(layer) ?? 'Turn this shape into points you can move'}
            onClick={() => {
              if (convertToPath(layer.id)) useEditor.getState().beginShaping(layer.id);
            }}
          >
            Edit points
          </button>
        </>
      )}

      {def.adjust === 'radius' && (
        <Number
          label="Corner"
          value={layer.radius}
          step={0.5}
          onChange={(v) => set('radius', Math.max(0, v))}
        />
      )}

      {def.adjust === 'points' && (
        <Number
          label={layer.shape === 'polygon' ? 'Sides' : 'Points'}
          value={layer.points}
          unit=""
          step={1}
          onChange={(v) => set('points', Math.max(3, Math.round(v)))}
        />
      )}

      {/* A line has no inside, so it is offered a colour rather than a fill. */}
      {!def.strokeOnly && (
        <FillField label="Fill" value={layer.fill} onChange={(fill) => set('fill', fill)} />
      )}

      <ColourField
        label={def.strokeOnly ? 'Colour' : 'Outline'}
        value={layer.stroke ? layer.stroke.colour : null}
        onChange={(colour) =>
          set('stroke', {
            colour,
            // A new outline needs a width, or it is set and invisible and the
            // customer concludes the control is broken.
            width: layer.stroke && layer.stroke.width > 0 ? layer.stroke.width : 0.5,
            dash: layer.stroke ? layer.stroke.dash : [],
          })
        }
        onClear={def.strokeOnly ? undefined : () => set('stroke', null)}
      />

      {/*
        Shadows, glows and the rest live under Effects now. A design saved with
        the older hard shadow still draws it, and says so here, because the
        control that made it has gone.
      */}
      {layer.shadow && !layer.effect && (
        <div className="tb-field">
          <span className="tb-field__label">Shadow</span>
          <button type="button" className="tb-btn tb-btn--quiet" onClick={() => set('shadow', null)}>
            Remove the older shadow
          </button>
        </div>
      )}

      {layer.stroke && (
        <div className="tb-field__row">
          <Number
            label="Width"
            value={layer.stroke.width}
            step={0.1}
            onChange={(v) =>
              set('stroke', { ...layer.stroke!, width: Math.max(0.05, v) })
            }
          />

          <label className="tb-field">
            <span className="tb-field__label">Line</span>
            <select
              className="tb-input"
              value={dashName(layer.stroke.dash)}
              onChange={(event) => set('stroke', { ...layer.stroke!, dash: DASHES[event.target.value] ?? [] })}
            >
              {Object.keys(DASHES).map((name) => (
                <option value={name} key={name}>
                  {name}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}
    </section>
  );
}

/** Dash patterns, in millimetres, named for what they look like. */
export const DASHES: Record<string, number[]> = {
  Solid: [],
  Dashed: [2, 1.2],
  Dotted: [0.4, 0.8],
  'Long dash': [4, 1.6],
};

export function dashName(dash: number[]): string {
  const found = Object.entries(DASHES).find(
    ([, pattern]) => pattern.length === dash.length && pattern.every((v, i) => v === dash[i])
  );

  return found ? found[0] : 'Solid';
}

function TextProperties({ layer }: { layer: TextLayer }) {
  const update = useEditor((s) => s.updateLayer);
  const stocks = familyHas(layer.font.family);

  const set = <K extends keyof TextLayer>(field: K, value: TextLayer[K]) =>
    update(layer.id, 'Change text', (l) => ({ ...(l as TextLayer), [field]: value }), {
      coalesce: `${layer.id}:${String(field)}`,
    });

  return (
    <section className="tb-props__section">
      <h2 className="tb-props__heading">Text</h2>

      <div className="tb-field">
        <span className="tb-field__label">Font</span>
        <FontField
          value={layer.font.family}
          onChange={(family) => {
            // Keep the look if the new family can hold it, and fall back
            // quietly if it cannot - switching to a family with no italic
            // should not leave the layer asking for one forever.
            const stock = familyHas(family);

            set('font', {
              ...layer.font,
              family,
              weight: layer.font.weight >= 600 && !stock.bold ? 400 : layer.font.weight,
              style: layer.font.style === 'italic' && !stock.italic ? 'normal' : layer.font.style,
            });
          }}
        />
      </div>

      <div className="tb-field__row">
        {/* Points, not millimetres. Type size is the one measurement in print
            that nobody quotes in mm. */}
        <Number
          label="Size"
          unit="pt"
          value={layer.font.size}
          step={0.5}
          onChange={(v) => set('font', { ...layer.font, size: Math.max(0.5, v) })}
        />
        <Number
          label="Lead"
          unit="×"
          value={layer.lineHeight}
          step={0.05}
          onChange={(v) => set('lineHeight', Math.max(0.5, Math.min(5, v)))}
        />
      </div>

      {/*
        Bold and Italic pick a real face. Faux-bold - stroking the outline - and
        faux-italic - shearing it - are what a browser does when a face is
        missing, and both look wrong in print: the stroke thickens the counters
        and the shear breaks the joins in Arabic. So a family with no such face
        says so rather than pretending.
      */}
      <div className="tb-field">
        <span className="tb-field__label">Style</span>
        <div className="tb-toggles">
          <button
            type="button"
            className={`tb-toggle${layer.font.weight >= 600 ? ' is-on' : ''}`}
            aria-pressed={layer.font.weight >= 600}
            disabled={!stocks.bold}
            title={stocks.bold ? 'Bold' : `${layer.font.family} has no bold face`}
            onClick={() => set('font', { ...layer.font, weight: layer.font.weight >= 600 ? 400 : 700 })}
          >
            <strong>B</strong>
          </button>

          <button
            type="button"
            className={`tb-toggle${layer.font.style === 'italic' ? ' is-on' : ''}`}
            aria-pressed={layer.font.style === 'italic'}
            disabled={!stocks.italic}
            title={stocks.italic ? 'Italic' : `${layer.font.family} has no italic face`}
            onClick={() =>
              set('font', { ...layer.font, style: layer.font.style === 'italic' ? 'normal' : 'italic' })
            }
          >
            <em>I</em>
          </button>

          <button
            type="button"
            className={`tb-toggle${layer.underline ? ' is-on' : ''}`}
            aria-pressed={layer.underline}
            title="Underline"
            onClick={() => set('underline', !layer.underline)}
          >
            <span style={{ textDecoration: 'underline' }}>U</span>
          </button>

          <button
            type="button"
            className={`tb-toggle${layer.strike ? ' is-on' : ''}`}
            aria-pressed={layer.strike}
            title="Strike through"
            onClick={() => set('strike', !layer.strike)}
          >
            <span style={{ textDecoration: 'line-through' }}>S</span>
          </button>
        </div>
      </div>

      <div className="tb-field">
        <span className="tb-field__label">Case</span>
        <div className="tb-toggles">
          {([
            ['AB', 'UPPER CASE', (t: string) => t.toUpperCase()],
            ['ab', 'lower case', (t: string) => t.toLowerCase()],
            ['Ab', 'Title Case', (t: string) =>
              t.replace(/\S+/g, (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())],
          ] as const).map(([label, title, change]) => (
            <button
              type="button"
              key={label}
              className="tb-toggle"
              title={title}
              // Changes the text itself rather than carrying a transform on the
              // layer: the customer can see what they will get, and can undo it.
              onClick={() => set('text', change(layer.text))}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Letter spacing, which the model has carried since Phase 2 with no
          way to reach it. Millimetres, like everything else here. */}
      <Number
        label="Track"
        unit="mm"
        value={layer.letterSpacing}
        step={0.05}
        onChange={(v) => set('letterSpacing', v)}
      />

      <label className="tb-field">
        <span className="tb-field__label">Align</span>
        <select
          className="tb-input"
          value={layer.align}
          onChange={(event) => set('align', event.target.value as TextLayer['align'])}
        >
          <option value="left">Left</option>
          <option value="center">Centre</option>
          <option value="right">Right</option>
          <option value="justify">Justify</option>
        </select>
      </label>

      <label className="tb-field">
        <span className="tb-field__label">Flow</span>
        <select
          className="tb-input"
          value={layer.direction}
          onChange={(event) => set('direction', event.target.value as TextLayer['direction'])}
        >
          <option value="auto">Automatic</option>
          <option value="ltr">Left to right</option>
          <option value="rtl">Right to left</option>
        </select>
      </label>

      <FillField label="Colour" value={layer.fill} onChange={(fill) => set('fill', fill)} />
    </section>
  );
}

function QrProperties({ layer }: { layer: QrLayer }) {
  const update = useEditor((s) => s.updateLayer);

  const set = <K extends keyof QrLayer>(field: K, value: QrLayer[K]) =>
    update(layer.id, 'Change QR code', (l) => ({ ...(l as QrLayer), [field]: value }), {
      coalesce: `${layer.id}:${String(field)}`,
    });

  const module = moduleMm(layer);
  const unit = useEditor((s) => s.unit);
  const tooFine = module > 0 && module < MIN_MODULE_MM;
  const wontFit = layer.value.trim() !== '' && !grid(layer);

  // A code saved before there were fields is shown as a form all the same. It
  // is only rewritten when something is changed (see fieldsFromValue).
  const answers = layer.fields && Object.keys(layer.fields).length > 0 ? layer.fields : fieldsFromValue(layer.kind, layer.value);

  return (
    <section className="tb-props__section">
      <h2 className="tb-props__heading">QR code</h2>

      <QrKindSelect value={layer.kind} onChange={(kind) => setQrContent(layer.id, kind, answers)} />

      <QrFields
        key={`${layer.id}:${layer.kind}`}
        kind={layer.kind}
        fields={answers}
        onChange={(fields) => setQrContent(layer.id, layer.kind, fields)}
      />

      <label className="tb-field">
        <span className="tb-field__label">Repair</span>
        <select
          className="tb-input"
          value={layer.ecc}
          onChange={(event) => set('ecc', event.target.value as QrLayer['ecc'])}
        >
          {/*
            Error correction, in the terms a shop thinks in. Higher repair means
            the code still scans when part of it is scuffed or covered, at the
            cost of more modules in the same space.
          */}
          <option value="L">Low — smallest code</option>
          <option value="M">Normal</option>
          <option value="Q">High</option>
          <option value="H">Highest — survives a logo on top</option>
        </select>
      </label>

      {wontFit && (
        <div className="tb-dpi tb-dpi--poor">
          <strong>Too much to encode</strong>
          <span>Shorten it, or lower the repair level.</span>
        </div>
      )}

      {tooFine && (
        <div className="tb-dpi tb-dpi--soft">
          <strong className="tb-num">{formatLength(module, unit)} squares</strong>
          <span>
            Below {formatLength(MIN_MODULE_MM, unit)} these blur together on paper and phones stop reading them. Make the
            code larger, shorten what it holds, or lower the repair level.
          </span>
        </div>
      )}
      {/*
        Both colours, because a QR is read by a machine that only cares about
        contrast. A dark code on a dark card does not scan, and the customer
        finds out after the cards are printed.
      */}
      <ColourField label="Code" value={layer.fill} onChange={(colour) => set('fill', colour)} />
      <ColourField
        label="Behind"
        value={layer.background}
        onChange={(colour) => set('background', colour)}
      />

    </section>
  );
}

function ImageProperties({ layer }: { layer: ImageLayer }) {
  const update = useEditor((s) => s.updateLayer);
  const asset = layer.asset ? knownAsset(layer.asset) : null;
  const cropping = useEditor((s) => s.cropping) === layer.id;

  const set = <K extends keyof ImageLayer>(field: K, value: ImageLayer[K]) =>
    update(layer.id, 'Change picture', (l) => ({ ...(l as ImageLayer), [field]: value }), {
      coalesce: `${layer.id}:${String(field)}`,
    });

  const dpi = asset ? effectiveDpi({ ...asset, kind: 'image', ref: asset.id, dpi: 0 }, layer.w) : 0;
  const verdict = dpiVerdict(dpi);

  return (
    <section className="tb-props__section">
      <h2 className="tb-props__heading">Picture</h2>

      {/*
        The number that decides whether this prints well, and the only place in
        the panel that is allowed to be alarming. It is a readout, not a
        control: there is no way to raise it but to supply a bigger file, and
        pretending otherwise would be a lie with a slider on it.
      */}
      {asset && (
        <div className={`tb-dpi tb-dpi--${verdict}`}>
          <strong className="tb-num">{dpi} dpi</strong>
          <span>
            {verdict === 'good'
              ? 'Sharp at this size.'
              : verdict === 'soft'
                ? 'A little soft at this size. Make it smaller, or send a bigger picture.'
                : 'Too low to print well. Please send a bigger picture, or make this much smaller.'}
          </span>
        </div>
      )}

      {cropping ? (
        <CropControls layer={layer} />
      ) : (
        <>
          <label className="tb-field">
            <span className="tb-field__label">Fit</span>
            <select
              className="tb-input"
              value={layer.fit}
              onChange={(event) => set('fit', event.target.value as ImageLayer['fit'])}
            >
              <option value="cover">Fill the box</option>
              <option value="contain">Show all of it</option>
              <option value="fill">Stretch</option>
            </select>
          </label>

          {asset && (
            <button
              type="button"
              className="tb-btn tb-crop__open"
              onClick={() => useEditor.getState().beginCrop(layer.id, layer.crop)}
            >
              Choose what shows
            </button>
          )}
        </>
      )}

      <div className="tb-field__row">
        <Number
          label="Light"
          unit="%"
          step={5}
          value={Math.round(layer.filters.brightness * 100)}
          onChange={(v) => set('filters', { ...layer.filters, brightness: clamp(v / 100) })}
        />
        <Number
          label="Punch"
          unit="%"
          step={5}
          value={Math.round(layer.filters.contrast * 100)}
          onChange={(v) => set('filters', { ...layer.filters, contrast: clamp(v / 100) })}
        />
      </div>

      <label className="tb-field">
        <span className="tb-field__label">Grey</span>
        <input
          type="checkbox"
          checked={layer.filters.greyscale}
          onChange={(event) => set('filters', { ...layer.filters, greyscale: event.target.checked })}
        />
      </label>

      {asset && (
        <p className="tb-props__note tb-num">
          {asset.w} × {asset.h} px
        </p>
      )}
    </section>
  );
}

const clamp = (value: number) => Math.max(-1, Math.min(1, value));

/* ---------------------------------------------------------------
 * Small controls
 * ------------------------------------------------------------ */

/**
 * A number, with what it is measured in beside it.
 *
 * `unit` left as 'mm' means a length, and a length is shown and typed in the
 * customer's own unit and handed back in millimetres - the caller never learns
 * that the customer thinks in inches. Anything else, degrees or a percentage or
 * the points type is set in, is shown exactly as it is.
 */
export function Number({
  label,
  value,
  onChange,
  unit = 'mm',
  step = 0.5,
}: {
  label: string;
  value: number;
  onChange: (value: number) => void;
  unit?: string;
  step?: number;
}) {
  const chosen = useEditor((s) => s.unit);
  const length = unit === 'mm';
  const suffix = length ? unitInfo(chosen).short : unit;
  const words = length ? unitInfo(chosen).name.toLowerCase() : unit;

  return (
    <label className="tb-field">
      <span className="tb-field__label">{label}</span>
      <span className={`tb-numbox${suffix ? ' tb-numbox--unit' : ''}`}>
        <input
          className="tb-input tb-num"
          type="number"
          inputMode="decimal"
          step={length ? stepIn(step, chosen) : step}
          value={length ? fromMm(value, chosen) : trim(value)}
          aria-label={words ? `${label} in ${words}` : label}
          onChange={(event) => {
            const next = parseFloat(event.target.value);

            if (!isNaN(next)) onChange(length ? toMm(next, chosen) : roundMm(next));
          }}
        />
        {suffix && (
          <span className="tb-numbox__unit" aria-hidden="true">
            {suffix}
          </span>
        )}
      </span>
    </label>
  );
}

function Readout({ label, value }: { label: string; value: string }) {
  return (
    <div className="tb-field">
      <span className="tb-field__label">{label}</span>
      <span className="tb-num">{value}</span>
    </div>
  );
}

/** Two decimals at most, and no trailing zeros - 8 mm, not 8.00 mm. */
function trim(value: number): number {
  return Math.round(value * 100) / 100;
}

/**
 * The panel while a picture is being cropped.
 *
 * Deliberately small. The gesture is on the canvas - drag the picture, roll the
 * wheel - and this is the part of it that a mouse without a wheel, or a hand
 * that would rather type a number, still needs.
 */
function CropControls({ layer }: { layer: ImageLayer }) {
  const asset = layer.asset ? knownAsset(layer.asset) : null;
  const ratio = asset ? shownRatio(layer, { w: asset.w, h: asset.h }) : 1;

  // How much of the picture is in the frame, as a percentage. Smaller is
  // closer in, so the slider is labelled by what it does rather than by the
  // number behind it.
  const most = Math.min(1, ratio > 0 ? 1 / ratio : 1);
  const closeness = Math.round((1 - layer.crop.h / most) * 100);

  const zoomTo = (percent: number) => {
    const h = most * (1 - Math.max(0, Math.min(95, percent)) / 100);

    useEditor.getState().updateLayer(
      layer.id,
      'Crop',
      (l) =>
        l.type === 'image'
          ? {
              ...l,
              // About the middle, so the slider does not also pan.
              crop: settle(
                {
                  x: l.crop.x - (ratio * h - l.crop.w) / 2,
                  y: l.crop.y - (h - l.crop.h) / 2,
                  w: ratio * h,
                  h,
                },
                ratio
              ),
            }
          : l,
      { coalesce: `crop:${layer.id}` }
    );
  };

  return (
    <>
      <p className="tb-props__hint">
        Drag the picture to choose what shows. The faded part is cut off.
      </p>

      <label className="tb-field">
        <span className="tb-field__label">Closer</span>
        {/* parseFloat rather than Number, which this file uses for a field. */}
        <input
          className="tb-range"
          type="range"
          min={0}
          max={95}
          value={closeness}
          onChange={(event) => zoomTo(parseFloat(event.target.value))}
        />
      </label>

      <div className="tb-field__row">
        <button
          type="button"
          className="tb-btn"
          onClick={() => useEditor.getState().endCrop(false)}
        >
          Leave it
        </button>
        <button
          type="button"
          className="tb-btn tb-btn--primary"
          onClick={() => useEditor.getState().endCrop()}
        >
          Done
        </button>
      </div>
    </>
  );
}

/**
 * A drawing's own controls: its points, and whether it is closed.
 *
 * The gestures are on the canvas. This is where they are explained, and where
 * the one that has no gesture - opening or closing the path - lives.
 */
function PathControls({ layer }: { layer: ShapeLayer }) {
  const shaping = useEditor((s) => s.shaping === layer.id);
  const count = layer.nodes?.length ?? 0;

  return (
    <>
      <div className="tb-field">
        <span className="tb-field__label">Points</span>
        <span className="tb-num">{count}</span>
      </div>

      <div className="tb-field__row">
        <button
          type="button"
          className={`tb-btn tb-points__open${shaping ? ' tb-btn--primary' : ''}`}
          disabled={layer.locked}
          aria-pressed={shaping}
          onClick={() => {
            const store = useEditor.getState();

            if (shaping) store.endShaping();
            else store.beginShaping(layer.id);
          }}
        >
          {shaping ? 'Done' : 'Edit points'}
        </button>

        <button
          type="button"
          className={`tb-toggle${layer.closed ? ' is-on' : ''}`}
          aria-pressed={!!layer.closed}
          disabled={layer.locked}
          title={layer.closed ? 'Open the path between its last point and its first' : 'Join the last point back to the first'}
          onClick={() =>
            useEditor.getState().updateLayer(layer.id, layer.closed ? 'Open the path' : 'Close the path', (l) =>
              l.type === 'shape' ? { ...l, closed: !l.closed } : l
            )
          }
        >
          {layer.closed ? 'Closed' : 'Open'}
        </button>
      </div>

      {shaping && (
        <p className="tb-props__hint">
          Drag a point or its handles. Alt-click a point to switch between corner and curve. Double-click the
          outline to add a point; Delete removes the chosen one.
        </p>
      )}
    </>
  );
}

function describe(layer: Layer): string {
  switch (layer.type) {
    case 'text':
      return 'Text';
    case 'image':
      return 'Picture';
    case 'shape':
      return 'Shape';
    case 'svg':
      return 'Graphic';
    case 'qr':
      return 'QR code';
    case 'barcode':
      return 'Barcode';
    case 'group':
      return 'Group';
    default:
      return 'Field';
  }
}
